import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLiff } from '@/hooks/useLiff';
import { validateInvitation, linkLine, ApiError } from '@/services';
import { Button, Loading, ErrorMessage, Card } from '@/components/common';
import { LinkConfirm } from '@/components/liff';
import type { InvitationValidateResponse } from '@/types/invitation';
import { INVITATION_ERROR_MESSAGES, type InvitationErrorCode, type InvitationEnv } from '@/types/invitation';
import { LINE_ERROR_MESSAGES, type LineErrorCode, type LineLinkResponse } from '@/types/line';
import { getFlaroAppUrl, getEnvironmentFromUrl } from '@/config/apiDomains';
import { isDebugMode } from '@/utils/debugLogger';
import liffSdk from '@line/liff';

type InternalState =
  | 'idle'
  | 'validating'
  | 'ready'
  | 'linking'
  | 'error';

interface DebugInfo {
  timestamp: string;
  event: string;
  data: Record<string, unknown>;
}

type EffectiveState =
  | 'initializing'
  | 'login_required'
  | 'validating'
  | 'ready'
  | 'linking'
  | 'error';

interface PageError {
  title: string;
  message: string;
  canRetry: boolean;
}

export const LinkPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const liff = useLiff();

  const [internalState, setInternalState] = useState<InternalState>('idle');
  const [invitation, setInvitation] = useState<InvitationValidateResponse | null>(null);
  const [error, setError] = useState<PageError | null>(null);
  const [debugLogs, setDebugLogs] = useState<DebugInfo[]>([]);

  // URLパラメータから取得、なければsessionStorage/liff.stateからフォールバック
  const token = useMemo(() => {
    const fromUrl = searchParams.get('token');
    if (fromUrl) return fromUrl;
    try { return sessionStorage.getItem('flaro_liff_token'); } catch { return null; }
  }, [searchParams]);
  const env = useMemo(() => {
    const fromUrl = searchParams.get('env') as InvitationEnv | null;
    if (fromUrl) return fromUrl;
    return getEnvironmentFromUrl() as InvitationEnv | null;
  }, [searchParams]);
  const debugMode = useMemo(() => isDebugMode(), []);

  // デバッグログ追加
  const addDebugLog = useCallback((event: string, data: Record<string, unknown>) => {
    if (!debugMode) return;
    setDebugLogs(prev => [...prev, {
      timestamp: new Date().toISOString(),
      event,
      data,
    }]);
  }, [debugMode]);

  // 有効な状態を計算（LIFFの状態とコンポーネントの状態から派生）
  const effectiveState = useMemo((): EffectiveState => {
    // LIFF読み込み中
    if (liff.isLoading) {
      return 'initializing';
    }

    // LIFFエラー
    if (liff.error) {
      return 'error';
    }

    // LIFF未初期化
    if (!liff.isInitialized) {
      return 'initializing';
    }

    // 未ログイン
    if (!liff.isLoggedIn) {
      return 'login_required';
    }

    // コンポーネント内部状態をそのまま返す
    if (internalState === 'idle') {
      return 'validating'; // idle状態の場合はvalidating開始前
    }

    return internalState as EffectiveState;
  }, [liff.isLoading, liff.error, liff.isInitialized, liff.isLoggedIn, internalState]);

  // 表示用エラー（LIFFエラーまたはコンポーネントエラー）
  const displayError = useMemo((): PageError | null => {
    if (liff.error) {
      return {
        title: 'LIFF初期化エラー',
        message: liff.error.message,
        canRetry: true,
      };
    }
    return error;
  }, [liff.error, error]);

  // 招待トークンの検証
  const validateToken = useCallback(async () => {
    addDebugLog('validateToken:start', { token: token?.substring(0, 8), env });

    if (!token) {
      addDebugLog('validateToken:no_token', {});
      setError({
        title: '招待トークンがありません',
        message: 'URLに招待トークンが含まれていません。QRコードを再度読み取ってください。',
        canRetry: false,
      });
      setInternalState('error');
      return;
    }

    if (!env) {
      addDebugLog('validateToken:no_env', {});
      setError({
        title: '環境パラメータがありません',
        message: 'URLに環境情報が含まれていません。QRコードを再度読み取ってください。',
        canRetry: false,
      });
      setInternalState('error');
      return;
    }

    setInternalState('validating');

    try {
      addDebugLog('validateToken:api_call', { token: token?.substring(0, 8) });
      const result = await validateInvitation(token);
      addDebugLog('validateToken:api_response', {
        valid: result.valid,
        error_code: result.error_code,
        error_message: result.error_message,
        companyCode: result.company_code,
        staffCode: result.staff_code,
      });

      // valid フィールドのチェック（error_code ベースでメッセージ出し分け）
      if (!result.valid) {
        const errorCode = result.error_code;
        addDebugLog('validateToken:invalid_invitation', { valid: result.valid, error_code: errorCode });
        const message = errorCode
          ? (INVITATION_ERROR_MESSAGES[errorCode] || result.error_message || 'この招待は無効です。管理者に確認してください。')
          : 'この招待は既に使用されたか、無効化されています。管理者に再発行を依頼してください。';
        setError({
          title: '招待が無効です',
          message,
          canRetry: errorCode === 'TOKEN_NOT_FOUND',
        });
        setInternalState('error');
        return;
      }

      setInvitation(result);
      setInternalState('ready');
    } catch (err) {
      addDebugLog('validateToken:error', {
        errorType: err instanceof ApiError ? 'ApiError' : typeof err,
        errorCode: err instanceof ApiError ? err.code : null,
        errorMessage: err instanceof Error ? err.message : String(err),
      });

      if (err instanceof ApiError) {
        const errorCode = err.code as InvitationErrorCode;
        const message =
          INVITATION_ERROR_MESSAGES[errorCode] || err.message;
        setError({
          title: '招待の確認に失敗しました',
          message,
          canRetry: errorCode === 'TOKEN_NOT_FOUND',
        });
      } else {
        setError({
          title: 'エラーが発生しました',
          message: '招待の確認中にエラーが発生しました。もう一度お試しください。',
          canRetry: true,
        });
      }
      setInternalState('error');
    }
  }, [token, env, addDebugLog]);

  // トークンがない場合はSSOページにリダイレクト
  // リッチメニューからのアクセス等、招待トークンなしの場合はSSOフローへ誘導
  // LIFF初期化完了を待ってからリダイレクト判定（liff.stateからのトークン復元を考慮）
  useEffect(() => {
    if (liff.isLoading) return;
    if (token === null) {
      addDebugLog('redirect_to_sso', {
        reason: 'no_invitation_token',
        searchParams: searchParams.toString(),
      });
      const ssoParams = new URLSearchParams(searchParams.toString());
      ssoParams.delete('token');
      const query = ssoParams.toString();
      navigate(query ? `/sso?${query}` : '/sso', { replace: true });
    }
  }, [token, liff.isLoading, navigate, searchParams, addDebugLog]);

  // LIFF初期化後の処理（ログイン済みの場合のみトークン検証を開始）
  useEffect(() => {
    // まだ準備ができていない場合は何もしない
    if (liff.isLoading || liff.error || !liff.isInitialized || !liff.isLoggedIn) {
      return;
    }

    // トークンなしの場合はリダイレクトuseEffectに任せる
    if (token === null) {
      return;
    }

    // idle状態の場合のみvalidateTokenを呼び出す（非同期で実行）
    if (internalState === 'idle') {
      // ESLint react-hooks/set-state-in-effect 回避のため queueMicrotask を使用
      queueMicrotask(() => {
        validateToken();
      });
    }
  }, [token, liff.isLoading, liff.error, liff.isInitialized, liff.isLoggedIn, internalState, validateToken]);

  // FLARO Webへリダイレクト（自動ログイン）
  const redirectToFlaroWeb = useCallback((result: LineLinkResponse) => {
    const flaroUrl = getFlaroAppUrl(env, result.company_code);
    const callbackUrl = new URL(`${flaroUrl}/auth/line-callback`);

    // フラグメントでトークンを渡す（サーバーに送信されない）
    const fragment = new URLSearchParams({
      access_token: result.access_token!,
      id_token: result.id_token!,
      refresh_token: result.refresh_token!,
      expires_in: result.expires_in!.toString(),
      email: result.email!,
    }).toString();

    callbackUrl.hash = fragment;

    // liff.openWindow({ external: true }) でフラグメントが消失する場合のフォールバック
    // flaro-web の LineCallback.tsx は _f クエリパラメータからもトークンを復元可能
    callbackUrl.searchParams.set('_f', fragment);

    addDebugLog('redirectToFlaroWeb', {
      flaroUrl,
      callbackUrlFull: callbackUrl.toString(),
      callbackUrlBase: `${flaroUrl}/auth/line-callback`,
      fragmentKeys: ['access_token', 'id_token', 'refresh_token', 'expires_in', 'email'],
      hasAccessToken: !!result.access_token,
      hasIdToken: !!result.id_token,
      hasRefreshToken: !!result.refresh_token,
      expiresIn: result.expires_in,
      email: result.email,
      isInClient: liff.isInClient,
    });

    // 履歴を置換してセキュリティを強化
    window.history.replaceState({}, document.title, window.location.pathname);

    // LINEアプリ内の場合は外部ブラウザで開く
    if (liff.isInClient) {
      try {
        liffSdk.openWindow({
          url: callbackUrl.toString(),
          external: true,
        });
        addDebugLog('openWindow_success', { url: callbackUrl.origin + callbackUrl.pathname });
        // 外部ブラウザで開いた後、LIFF画面を自動的に閉じる
        setTimeout(() => {
          try {
            liffSdk.closeWindow();
          } catch {
            // closeWindowが失敗した場合はユーザーが手動で閉じる
          }
        }, 300);
      } catch (openError) {
        addDebugLog('openWindow_failed', {
          error: openError instanceof Error ? openError.message : String(openError),
          url: callbackUrl.origin + callbackUrl.pathname,
        });
        // フォールバック: window.openを試す
        addDebugLog('trying_window_open_fallback', { url: callbackUrl.origin + callbackUrl.pathname });
        try {
          const newWindow = window.open(callbackUrl.toString(), '_blank');
          if (newWindow) {
            addDebugLog('window_open_success', { url: callbackUrl.origin + callbackUrl.pathname });
            setTimeout(() => {
              try {
                liffSdk.closeWindow();
              } catch {
                // closeWindowが失敗した場合はユーザーが手動で閉じる
              }
            }, 300);
          } else {
            // ポップアップがブロックされた可能性
            addDebugLog('window_open_blocked', { url: callbackUrl.origin + callbackUrl.pathname });
            setError({
              title: 'リダイレクトエラー',
              message: '外部ブラウザを開けませんでした。ポップアップがブロックされている可能性があります。',
              canRetry: false,
            });
            setInternalState('error');
          }
        } catch (fallbackError) {
          addDebugLog('window_open_fallback_failed', {
            error: fallbackError instanceof Error ? fallbackError.message : String(fallbackError),
          });
          setError({
            title: 'リダイレクトエラー',
            message: '外部ブラウザを開けませんでした。LINEアプリを最新版に更新してお試しください。',
            canRetry: false,
          });
          setInternalState('error');
        }
      }
    } else {
      window.location.href = callbackUrl.toString();
    }
  }, [env, liff, addDebugLog]);

  // LINE連携実行
  const handleLink = async () => {
    if (!invitation || !liff.accessToken) {
      addDebugLog('handleLink:validation_failed', {
        hasInvitation: !!invitation,
        hasAccessToken: !!liff.accessToken,
      });
      return;
    }

    addDebugLog('handleLink:start', {
      invitationToken: invitation.invitation_token,
      companyCode: invitation.company_code,
      staffCode: invitation.staff_code,
    });

    setInternalState('linking');

    try {
      const result = await linkLine({
        invitation_token: invitation.invitation_token,
        line_access_token: liff.accessToken,
        line_id_token: liff.idToken || undefined,
      });

      addDebugLog('handleLink:api_response', {
        success: result.success,
        companyCode: result.company_code,
        staffCode: result.staff_code,
        staffName: result.staff_name,
        lineUserId: result.line_user_id,
        lineDisplayName: result.line_display_name,
        cognitoUserCreated: result.cognito_user_created,
        hasIdToken: !!result.id_token,
        hasAccessToken: !!result.access_token,
        hasRefreshToken: !!result.refresh_token,
        expiresIn: result.expires_in,
        email: result.email,
        message: result.message,
      });

      const hasAllTokens = result.id_token && result.access_token && result.refresh_token && result.email;

      // デバッグモードの場合は常に完了ページへ遷移（トークンの有無を確認できるように）
      if (debugMode) {
        addDebugLog('handleLink:navigate_to_complete_debug', {
          reason: 'debug_mode',
          hasAllTokens,
        });
        navigate('/complete', {
          state: {
            staffName: result.staff_name,
            lineDisplayName: result.line_display_name,
            env: env,
            companyCode: invitation.company_code,
            debugInfo: {
              apiResponse: result,
              debugLogs: debugLogs.map(log => `[${log.timestamp}] ${log.event}: ${JSON.stringify(log.data)}`),
            },
          },
        });
      } else if (hasAllTokens) {
        // 本番モードでトークンが返ってきた場合は自動ログイン
        addDebugLog('handleLink:redirect_to_flaro', {
          reason: 'tokens_present',
        });
        redirectToFlaroWeb(result);
      } else {
        addDebugLog('handleLink:navigate_to_complete', {
          reason: 'tokens_missing',
          missingFields: {
            id_token: !result.id_token,
            access_token: !result.access_token,
            refresh_token: !result.refresh_token,
            email: !result.email,
          },
        });
        // トークンがない場合は従来通り完了ページへ
        navigate('/complete', {
          state: {
            staffName: result.staff_name,
            lineDisplayName: result.line_display_name,
            env: env,
            companyCode: invitation.company_code,
          },
        });
      }
    } catch (err) {
      addDebugLog('handleLink:error', {
        errorType: err instanceof ApiError ? 'ApiError' : 'Unknown',
        errorCode: err instanceof ApiError ? err.code : null,
        errorMessage: err instanceof Error ? err.message : String(err),
      });

      if (err instanceof ApiError) {
        const errorCode = err.code as LineErrorCode;
        const message = LINE_ERROR_MESSAGES[errorCode] || err.message;
        setError({
          title: 'LINE連携に失敗しました',
          message,
          canRetry: !['LINE_ALREADY_LINKED', 'EMAIL_REQUIRED'].includes(errorCode),
        });
      } else {
        setError({
          title: 'エラーが発生しました',
          message: 'LINE連携中にエラーが発生しました。もう一度お試しください。',
          canRetry: true,
        });
      }
      setInternalState('error');
    } finally {
      // 成功・失敗に関わらず使用済みトークンをsessionStorageから削除
      try { sessionStorage.removeItem('flaro_liff_token'); } catch { /* ignore */ }
    }
  };

  // LINEログイン
  const handleLogin = () => {
    liff.login();
  };

  // リトライ
  const handleRetry = () => {
    setError(null);
    setInternalState('idle');
  };

  // 閉じる
  const handleClose = () => {
    liff.closeLiff();
  };

  // レンダリング
  const renderContent = () => {
    switch (effectiveState) {
      case 'initializing':
      case 'validating':
        return (
          <Loading
            fullScreen
            message={
              effectiveState === 'initializing'
                ? 'LINEと接続中...'
                : '招待を確認中...'
            }
          />
        );

      case 'login_required':
        return (
          <Card className="text-center">
            <div className="space-y-6">
              <div>
                <div className="w-20 h-20 bg-line/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-line" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.064-.021.136-.031.2-.031.211 0 .391.09.51.25l2.437 3.311V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">LINEログインが必要です</h2>
                <p className="text-gray-500 mt-2">
                  LINE連携を行うにはLINEアカウントでログインしてください
                </p>
              </div>
              <Button variant="line" fullWidth onClick={handleLogin}>
                LINEでログイン
              </Button>
            </div>
          </Card>
        );

      case 'ready':
        if (!invitation || !liff.profile) {
          return (
            <Card>
              <ErrorMessage
                title="データの読み込みに失敗しました"
                message="招待情報またはプロフィール情報が取得できませんでした。"
              >
                <div className="flex gap-3 mt-4">
                  <Button variant="primary" size="sm" onClick={handleRetry}>
                    再試行
                  </Button>
                  <Button variant="secondary" size="sm" onClick={handleClose}>
                    閉じる
                  </Button>
                </div>
              </ErrorMessage>
            </Card>
          );
        }
        return (
          <div className="space-y-6">
            <LinkConfirm
              lineProfile={liff.profile}
              email={liff.email}
              invitation={invitation}
            />
            <div className="space-y-3">
              <Button
                variant="line"
                fullWidth
                onClick={handleLink}
              >
                連携する
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={handleClose}
              >
                キャンセル
              </Button>
            </div>
          </div>
        );

      case 'linking':
        return <Loading fullScreen message="LINE連携を実行中..." />;

      case 'error':
        return (
          <Card>
            <ErrorMessage
              title={displayError?.title || 'エラー'}
              message={displayError?.message || 'エラーが発生しました'}
            >
              <div className="flex gap-3 mt-4">
                {displayError?.canRetry && (
                  <Button variant="primary" size="sm" onClick={handleRetry}>
                    再試行
                  </Button>
                )}
                <Button variant="secondary" size="sm" onClick={handleClose}>
                  閉じる
                </Button>
              </div>
            </ErrorMessage>
          </Card>
        );

      default:
        // 予期しない状態のログ出力とフォールバック
        console.error('Unexpected effectiveState:', effectiveState);
        return (
          <Loading fullScreen message="読み込み中..." />
        );
    }
  };

  // デバッグパネル
  const renderDebugPanel = () => {
    if (!debugMode) return null;

    return (
      <div className="mt-8 p-4 bg-gray-900 text-green-400 rounded-lg text-xs font-mono overflow-auto max-h-96">
        <div className="text-yellow-400 font-bold mb-2">🔧 DEBUG MODE</div>

        {/* 現在の状態 */}
        <div className="mb-4 p-2 bg-gray-800 rounded">
          <div className="text-cyan-400 font-bold mb-1">Current State:</div>
          <div>effectiveState: {effectiveState}</div>
          <div>internalState: {internalState}</div>
          <div>token: {token ? `${token.substring(0, 8)}...` : 'null'}</div>
          <div>env: {env || 'null'}</div>
          <div>liff.isInitialized: {String(liff.isInitialized)}</div>
          <div>liff.isLoggedIn: {String(liff.isLoggedIn)}</div>
          <div>liff.isInClient: {String(liff.isInClient)}</div>
          <div>liff.hasAccessToken: {String(!!liff.accessToken)}</div>
          <div>invitation: {invitation ? 'loaded' : 'null'}</div>
        </div>

        {/* ログ */}
        <div className="text-cyan-400 font-bold mb-1">Event Logs ({debugLogs.length}):</div>
        {debugLogs.length === 0 ? (
          <div className="text-gray-500">No events yet</div>
        ) : (
          <div className="space-y-2">
            {debugLogs.map((log, index) => (
              <div key={index} className="p-2 bg-gray-800 rounded">
                <div className="text-yellow-400">[{log.timestamp}] {log.event}</div>
                <pre className="text-green-300 whitespace-pre-wrap break-all">
                  {JSON.stringify(log.data, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* ヘッダー */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">FLARO LINE連携</h1>
          <p className="text-sm text-gray-500 mt-1">
            LINEアカウントとFLAROを連携します
          </p>
          {debugMode && (
            <div className="mt-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded inline-block">
              🔧 デバッグモード有効
            </div>
          )}
        </div>

        {/* コンテンツ */}
        {renderContent()}

        {/* デバッグパネル */}
        {renderDebugPanel()}
      </div>
    </div>
  );
};

export default LinkPage;
